import { injectable } from 'inversify';
import { injectToken, Token } from 'inversify-token';
import { YinzCamAPI, YinzCamAPIRequest, YinzCamAPIRequestComponent, YinzCamAPIResponse, YinzCamAPIToken, YinzCamServer } from 'yinzcam-api';

export const YinzCamSignonServerToken = new Token<YinzCamSignonServer>(Symbol.for("YinzCamSignonServerToken"));

@injectable()
export class YinzCamSignonServer {
  private readonly server: YinzCamServer;

  public constructor(@injectToken(YinzCamAPIToken) private readonly api: YinzCamAPI) {
    this.server = api.getServer('signon');
  }

  public getRequest(request: YinzCamAPIRequest) {
    return this.server.getRequest(request);
  }

  public async postTicket(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'POST', path: 'ticket', data });
  }

  public async postRegister(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'POST', path: 'register', data });
  }

  public async postPasswordResetRequest(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'post', path: `password/reset`, data, requiredHeaders: [ 'X-YinzCam-Ticket' ] });
  }

  /* TODO: update api to allow you to either wait for a response (on-shot) or subscribe to updates */
  public async getProfile2UserSegment(ticket: string, segment: string): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'get', path: `profile2/user/segments/${segment}`, headers: { 'X-YinzCam-Ticket': ticket } });
  }

  public getProfile2UserSegmentComponent(segment: string): YinzCamAPIRequestComponent {
    return this.server.getRequest({ path: `profile2/user/segments/${segment}` });
  }

  public async postProfile2UserSegment(ticket: string, segment: string, data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'post', path: `profile2/user/segments/${segment}`, data, headers: { 'X-YinzCam-Ticket': ticket } });
  }

  public async postUploadImageSegment(ticket: string, segment: string, data: FormData): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'post', path: `upload/image/${segment}`, data, headers: { 'X-YinzCam-Ticket': ticket } });
  }

  public getMember(): YinzCamAPIRequestComponent {
    return this.server.getRequest({ path: 'member' });
  }

  public async getPasswordlessTokenValidate(token: string): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'get', path: `passwordless/token/validate`, params: { token } });
  }

  public async getPasswordlessStatusPoll(otp: string): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'get', path: `passwordless/status/poll`, params: { otp } });
  }

  public async getPasswordlessResend(otp: string): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'get', path: `passwordless/resend`, params: { otp } });
  }

  public async postVerifyRequest(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'POST', path: 'verify', data });
  }

  public async postPasswordResetBegin(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'post', path: `password/reset/begin`, data });
  }

  public async postPasswordResetValidate(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'post', path: `password/reset/validate`, data });
  }

  public async postPasswordResetEnd(data: object): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ allowAllStatusCodes: true, method: 'post', path: `password/reset/end`, data });
  }

  public async getRegisterAvailableRequest(identity: string): Promise<YinzCamAPIResponse> {
    return this.server.singleRequest({ method: 'get', path: `register/available`, params: { identity } });
  }
}
